<template>
  <div
    class="tree-menu"
    :class="`depth${depth}`"
  >
    <vue-client-only>
      <mobile-menu-header
        :title="headerTitle"
        :show-back="depth > 1"
        @back="back"
      />
    </vue-client-only>

    <slot v-if="depth === 1" name="prepend" />
    <div class="tree-menu-container">
      <ul
        :class="{ 'root-categories': depth === 1 }"
        v-for="(group, i) in items"
        :key="i"
      >
        <li
          v-for="item in group"
          :key="item.url"
          :class="{active: !!item.url && item.url === activeItem.url}"
        >
          <a
            :href="item.url"
            class="link"
            :style="{ color: item.color }"
            @click="itemClick($event, item)"
            v-text="item.name"
          />
          <tree-menu
            v-if="item.items && item.items.length && depth < maxDepth"
            :items="item.items"
            :depth="depth + 1"
            :max-depth="maxDepth"
            :path="[...path, item.name]"
          />
        </li>
      </ul>
    </div>
    <slot v-if="depth === 1" name="append" />
  </div>
</template>

<script>
import VueClientOnly from 'vue-client-only';

import MobileMenuHeader from './mobile-menu-header.vue';
import { headerGtmEvents } from '@/gtm/header-gtm-events';

export default {
  name: 'tree-menu',

  components: {
    MobileMenuHeader,
    VueClientOnly,
  },

  inject: ['adaptive'],

  props: {
    items: {
      type: Array,
      default: () => ([]),
    },
    depth: {
      type: Number,
      default: 1,
    },
    maxDepth: {
      type: Number,
      default: 6,
    },
    path: {
      type: Array,
      default: () => [],
    }
  },

  data: () => ({
    activeItem: {},
  }),

  computed: {
    headerTitle() {
      return this.depth > 1 && this.$parent.activeItem?.name
        ? this.$parent.activeItem.name
        : ''
      ;
    }
  },

  methods: {
    itemClick(e, item) {
      if (
        !this.adaptive.isDesktop
        && this.depth < this.maxDepth
        && item.items.length
      ) {
        e.preventDefault();
        this.setActiveItem(item);
        this.$f.showNav(); // тут тригается скролл
        headerGtmEvents.clickMenuItem({ path: [...this.path, item.name, 'БургерМеню'] });
      } else {
        headerGtmEvents.clickMenuItem({ path: [...this.path, item.name] });
      }
    },
    setActiveItem (item = {}) {
      this.activeItem = item;
    },
    back() {
      this.$parent.setActiveItem();
    },
  },
}
</script>

<style lang="scss">
.tree-menu {
  overflow: auto;
  ul {
    margin: 0;
    padding: 0;
    list-style: none;
    > li {
      font-size: 16px;
      line-height: 24px;
    }
  }
  .active {
    > div {
      transform: translateX(0);
      box-shadow: 0 0 20px 0 rgba($black, .2);
      /* фикс меню в safari ios */
      visibility: visible;
    }
  }
  .root-categories {
    margin: $containerGapMobile;
    margin-bottom: 0;
    &::before {
      content: 'Каталог товаров';
      display: block;
      font-size: 14px;
      line-height: 24px;
      color: $gray;
    }
  }
  .link {
    position: relative;
    display: flex;
    padding: 12px $containerGapMobile;
    color: $black;
    font-weight: 600;
    font-size: 14px;
    text-decoration: none;
    align-items: center;
    &::after {
      position: absolute;
      right: 0;
      bottom: 0;
      left: 0;
      display: block;
      box-shadow: 0 0 0 var(--hairline) $gray_lu;
      content: "";
    }
    .sprite-icon {
      width: 24px;
      height: 24px;
      margin-right: 12px;
    }
    .ui-badge {
      margin-left: auto;
    }
  }
  &.depth1 {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 100%;
  }
  &:not(.depth1) {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: $white;
    transform: translateX(100%);
    transition: all .2s ease-in-out;
    z-index: 1;
    /* фикс меню в safari ios */
    visibility: hidden;
  }
}

@include breakpoint("desktop") {
  // TODO рефакторинг по стилям
  // https://jira.app.local/browse/ESMFUN-674
  .tree-menu {
    overflow: hidden;
    // рут
    &.depth1 {
      display: block;
      min-height: 0;
      > .tree-menu-container {
        > ul {
          display: flex;
          max-width: $maxWidthContainer;
          margin: 0 auto;
          padding: 0 $containerGapDesktop;
          > li {
            &:first-child a {
              margin-left: 0;
            }
            &:hover {
              > a {
                position: relative;
                &::after {
                  content: '';
                  display: block;
                  position: absolute;
                  left: 0;
                  right: 0;
                  bottom: -1px;
                  height: 2px;
                  background-color: $black;
                }
              }
              .tree-menu {
                z-index: 99; // под ui-dropdown
                display: flex;
              }
            }
          }
          .link {
            padding: 12px 0;
            margin: 0 24px;
            &::after {
              display: none;
            }
          }
        }
      }
    }
    &.depth2 {
      position: absolute;
      top: 48px;
      right: 0;
      bottom: auto;
      left: 0;
      display: none;
      background-color: $white;
      padding: 36px 0;
      box-shadow: inset 0 1px 0 #e0e0e0, 0 16px 16px rgba(0, 0, 0, .08);
      > .tree-menu-container {
        width: $maxWidthContainer;
        max-width: $maxWidthContainer;
        margin: 0 auto;
        padding: 0 $containerGapDesktop;
        display: flex;
        text-align: left;
        > ul {
          flex: 0 0 20%;
          max-width: $maxWidthContainer;
          padding: 0 $containerGapDesktop;
          &:first-child {
            padding-left: 0;
          }
          .link {
            margin: 0;
          }
          > li:first-child > a {
            margin: 16px 0;
            padding: 0;
            font-size: 16px;
            &[href=''] {
              pointer-events: none;
            }
          }
        }
      }
    }
    &:not(.depth1) {
      transform: none;
      /* фикс меню в safari ios */
      visibility: visible;
    }
    &.depth3 {
      position: relative;
      .link {
        font-weight: normal;
        font-size: 16px;
        line-height: 20px;
        padding: 6px 0 !important;
      }
    }
    .root-categories {
      &::before {
        display: none;
      }
    }
  }
}

// Дубляж в шапке для планшета
.tablet-menu {
  flex: 1;
  .tree-menu {
    height: 24px;
    overflow: hidden;
    .mobile-menu-header {
      display: none;
    }
    .link {
      font-size: 12px;
      line-height: 16px;
      padding: 4px 12px;
      &::after {
        display: none;
      }
    }
    .root-categories {
      display: flex;
      flex-wrap: wrap;
      margin-top: 0;
      &::before {
        display: none;
      }
    }
  }
  ~ .icons {
    > * {
      margin-left: 20px !important;
    }
  }
}
</style>
